import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Typography, Button, Paper } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { handleError, createAppError, ErrorCode, getUserFriendlyErrorMessage, AppError } from '../utils/errorHandler.js';

interface Props {
  children: ReactNode;
  fallback?: ReactNode | ((error: AppError) => ReactNode);
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  showDetails?: boolean;
}

interface State {
  hasError: boolean;
  appError?: AppError;
}

/**
 * Enhanced ErrorBoundary component with tenant-aware error handling
 * 
 * Features:
 * - Captures and formats errors with tenant context
 * - Provides standardized error logging
 * - Supports custom fallback UI
 * - Optional error callback for analytics/reporting
 */
class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Convert to AppError with tenant context
    const appError = createAppError(
      error.message,
      ErrorCode.RENDERING_ERROR,
      error
    );
    
    // Update state so the next render will show the fallback UI
    return { hasError: true, appError };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Create standardized AppError with tenant context
    const appError = createAppError(
      error.message,
      ErrorCode.RENDERING_ERROR,
      error,
      { componentStack: errorInfo.componentStack }
    );
    
    // Use our standardized error handler
    handleError(appError);
    
    // Call onError callback if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleReset = (): void => {
    this.setState({ hasError: false, appError: undefined });
  };

  render(): ReactNode {
    if (this.state.hasError && this.state.appError) {
      const { appError } = this.state;
      
      // If custom fallback is a function, call it with the error
      if (typeof this.props.fallback === 'function') {
        return (this.props.fallback as (error: AppError) => ReactNode)(appError);
      }
      
      // If custom fallback is a ReactNode, use it
      if (this.props.fallback) {
        return this.props.fallback;
      }
      
      // Default fallback UI with tenant context
      return (
        <Box 
          display="flex" 
          flexDirection="column" 
          alignItems="center" 
          justifyContent="center" 
          height="100%" 
          p={3}
        >
          <Paper 
            elevation={3} 
            sx={{ 
              p: 4, 
              maxWidth: 500, 
              textAlign: 'center',
              borderLeft: '4px solid #f44336'
            }}
          >
            <ErrorOutlineIcon color="error" sx={{ fontSize: 60, mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              Something went wrong
            </Typography>
            <Typography variant="body1" color="textSecondary" paragraph>
              {getUserFriendlyErrorMessage(appError)}
            </Typography>
            {appError.tenant_id && (
              <Typography variant="caption" color="textSecondary" display="block">
                Tenant ID: {appError.tenant_id}
              </Typography>
            )}
            {appError.traceId && (
              <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
                Reference ID: {appError.traceId}
              </Typography>
            )}
            <Button 
              variant="contained" 
              color="primary" 
              startIcon={<RefreshIcon />}
              onClick={this.handleReset}
            >
              Try Again
            </Button>
          </Paper>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
